import { useCallback, useEffect, useState } from 'react'
import { toast } from 'sonner'
import {
  ACTION_FOR_STAGE,
  DETAIL_UNLOCK_FOR_STAGE,
  STATUS_FOR_STAGE,
} from '@/components/dashboard/data/deals'
import {
  completeDealPriorityAction,
  createDeal,
  deleteDealApi,
  dismissDealPriorityAction,
  getDeals,
  snoozeDealPriorityAction,
  updateDealApi,
} from '@/lib/api'
import { DealPipelineContext } from '@/lib/deal-pipeline-context'
import { useSession } from '@/lib/session-context'

const MOCK_DEAL_IMAGE = 'https://images.unsplash.com/photo-1570129477492-45c003edd2be?auto=format&fit=crop&w=300&q=60'
const ACTIVITY_SOURCES = new Set(['manual', 'system'])
const UPDATE_PAYLOAD_KEYS = [
  'address',
  'activationPostcodeTargetId',
  'expectedContextRevision',
  'sourceUrl',
  'sourceLabel',
  'hotLead',
  'stage',
  'status',
  'outcome',
  'lostReason',
  'detailUnlockStage',
  'selectedStrategy',
  'appraisalSummary',
  'negotiationSummary',
  'activitySource',
]

function scoreForDeal(address, isHotLead) {
  if (isHotLead) return 95 + (address.length % 5)

  return 75 + (address.length % 20)
}

function normalizeActivitySource(source) {
  return ACTIVITY_SOURCES.has(source) ? source : 'manual'
}

function snoozeDurationLabel(snoozeUntil, now = Date.now()) {
  const durationMinutes = Math.max(
    1,
    Math.round((new Date(snoozeUntil).getTime() - now) / (60 * 1000)),
  )

  if (durationMinutes < 48 * 60 && durationMinutes % 60 === 0) {
    const hours = durationMinutes / 60
    return `${hours} ${hours === 1 ? 'hour' : 'hours'}`
  }

  if (durationMinutes % (24 * 60) === 0) {
    const days = durationMinutes / (24 * 60)
    return `${days} ${days === 1 ? 'day' : 'days'}`
  }

  return `${durationMinutes} ${durationMinutes === 1 ? 'minute' : 'minutes'}`
}

function sourceLinkForDeal(deal) {
  return deal.sourceLink ?? deal.sourceUrl ?? ''
}

function lastActivityLabel(deal) {
  if (deal.lastActivity) return deal.lastActivity
  if (Number.isInteger(deal.inactivityDays)) {
    if (deal.inactivityDays === 0) return 'Today'
    if (deal.inactivityDays === 1) return '1 day ago'
    return `${deal.inactivityDays} days ago`
  }

  return undefined
}

function isImageMediaFile(file) {
  const contentType = file.contentType?.toLowerCase() || ''
  const documentType = file.documentType?.toLowerCase() || ''

  return contentType.startsWith('image/') || documentType === 'image'
}

function primaryImageForDeal(deal) {
  if (deal.primaryImage?.url) return deal.primaryImage

  return deal.mediaFiles?.find((file) => file.documentType === 'Image' && isImageMediaFile(file)) ??
    deal.mediaFiles?.find(isImageMediaFile) ??
    null
}

function normalizeOutcome(deal, stage) {
  if (deal.outcome) return deal.outcome
  if (deal.isWonOrLost) return deal.isWonOrLost
  if (stage === 'Completed') return 'Won'

  return 'Open'
}

function normalizeDeal(deal) {
  const address = deal.address ?? ''
  const hotLead = Boolean(deal.hotLead ?? deal.isHotLead)
  const stage = deal.stage ?? 'Identified'
  const outcome = normalizeOutcome(deal, stage)
  const score = Object.hasOwn(deal, 'score') ? deal.score : scoreForDeal(address, hotLead)
  const primaryImage = primaryImageForDeal(deal)
  const hasGenuineImage = Boolean(primaryImage?.url || (deal.image && deal.imageIsPlaceholder !== true))

  return {
    ...deal,
    address,
    activationPostcodeTargetId: deal.activationPostcodeTargetId ?? null,
    postcodeArea: deal.postcodeArea ?? null,
    hotLead,
    isHotLead: hotLead,
    sourceLink: sourceLinkForDeal(deal),
    image: hasGenuineImage ? (primaryImage?.url ?? deal.image) : MOCK_DEAL_IMAGE,
    imageIsPlaceholder: !hasGenuineImage,
    primaryImage,
    priorityActions: deal.priorityActions ?? [],
    stage,
    outcome,
    status: deal.status ?? STATUS_FOR_STAGE[stage] ?? 'New',
    action: deal.action ?? ACTION_FOR_STAGE[stage] ?? 'View Details',
    detailUnlockStage: deal.detailUnlockStage ?? DETAIL_UNLOCK_FOR_STAGE[stage] ?? 1,
    score,
    lastActivity: lastActivityLabel(deal),
    negotiationStatus: deal.negotiationStatus ?? deal.negotiationSummary,
    isWonOrLost: ['Won', 'Lost'].includes(outcome) ? outcome : undefined,
  }
}

function buildMockDeal(values, currentDeals) {
  const address = values.address.trim()
  const sourceLink = values.sourceLink.trim()

  return normalizeDeal({
    id: `mock-${currentDeals.length + 1}`,
    address,
    sourceLink,
    image: MOCK_DEAL_IMAGE,
    stage: 'Identified',
    outcome: 'Open',
    status: STATUS_FOR_STAGE.Identified,
    action: ACTION_FOR_STAGE.Identified,
    detailUnlockStage: 1,
    hotLead: values.isHotLead,
    score: scoreForDeal(address, values.isHotLead),
  })
}

function moveDealToStage(deal, targetStage) {
  let nextOutcome = deal.outcome
  if (nextOutcome === 'Open' && targetStage === 'Completed') nextOutcome = 'Won'

  const nextDeal = {
    ...deal,
    stage: targetStage,
    outcome: nextOutcome,
    action: ACTION_FOR_STAGE[targetStage] ?? deal.action,
    status: STATUS_FOR_STAGE[targetStage] ?? deal.status,
    detailUnlockStage: DETAIL_UNLOCK_FOR_STAGE[targetStage] ?? deal.detailUnlockStage ?? 1,
    isWonOrLost: nextOutcome === 'Won' ? 'Won' : undefined,
  }

  if (nextOutcome === 'Won') nextDeal.inactivityDays = 0

  return normalizeDeal(nextDeal)
}

function mergeDealUpdates(deal, updates) {
  const nextDeal = { ...deal, ...updates }
  if (updates.stage) nextDeal.action = ACTION_FOR_STAGE[updates.stage] ?? deal.action

  return normalizeDeal(nextDeal)
}

function createDealPayload(values) {
  return {
    address: values.address,
    sourceUrl: values.sourceLink,
    hotLead: values.isHotLead,
    activitySource: 'manual',
  }
}

function updateDealPayload(updates, activitySource) {
  const payload = { ...updates, activitySource: normalizeActivitySource(activitySource) }

  if (payload.sourceLink !== undefined) {
    payload.sourceUrl = payload.sourceLink
    delete payload.sourceLink
  }
  if (payload.isHotLead !== undefined) {
    payload.hotLead = payload.isHotLead
    delete payload.isHotLead
  }
  if (payload.negotiationStatus !== undefined) {
    payload.negotiationSummary = payload.negotiationStatus
    delete payload.negotiationStatus
  }

  return Object.fromEntries(
    UPDATE_PAYLOAD_KEYS
      .filter((key) => payload[key] !== undefined)
      .map((key) => [key, payload[key]]),
  )
}

function summaryForDeals(deals) {
  const activeDeals = deals.filter((deal) => deal.outcome === 'Open')

  return {
    activeDealsCount: activeDeals.length,
    appraisingCount: activeDeals.filter((deal) => deal.stage === 'Appraising').length,
    offersSubmittedCount: activeDeals.filter((deal) => (
      ['Offer Ready', 'In Negotiation', 'Follow Up', 'Legal / Due Diligence', 'Completed'].includes(deal.stage)
    )).length,
    dailyPriorities: [],
  }
}

export function DealPipelineProvider({
  children,
  initialDeals = [],
  shouldHydrate = true,
}) {
  const { currentUser, csrfToken } = useSession()
  const userId = currentUser?.id ?? null
  const hydrationUserId = shouldHydrate ? userId : null
  const canHydrate = Boolean(hydrationUserId)
  const [deals, setDeals] = useState(() => initialDeals.map(normalizeDeal))
  const [serverSummary, setServerSummary] = useState(null)
  const [isLoading, setIsLoading] = useState(canHydrate)
  const [error, setError] = useState(null)
  const summary = serverSummary ?? summaryForDeals(deals)

  const applyDealsPayload = useCallback((payload) => {
    const nextDeals = (payload.deals ?? []).map(normalizeDeal)
    setDeals(nextDeals)
    setServerSummary(payload.summary ?? null)
  }, [])

  const refreshDeals = useCallback(async () => {
    if (!hydrationUserId) return null

    const payload = await getDeals({ csrfToken })
    applyDealsPayload(payload)
    return payload
  }, [applyDealsPayload, csrfToken, hydrationUserId])

  useEffect(() => {
    if (!hydrationUserId) return undefined

    let active = true

    getDeals({ csrfToken })
      .then((payload) => {
        if (!active) return
        applyDealsPayload(payload)
      })
      .catch((fetchError) => {
        if (!active) return
        setError(fetchError.message)
      })
      .finally(() => {
        if (active) setIsLoading(false)
      })

    return () => { active = false }
  }, [applyDealsPayload, csrfToken, hydrationUserId])

  const addDeal = async (values) => {
    setError(null)

    if (!canHydrate) {
      const mockDeal = buildMockDeal(values, deals)
      setServerSummary(null)
      setDeals((currentDeals) => [mockDeal, ...currentDeals])
      return mockDeal
    }

    const payload = await createDeal(createDealPayload(values), { csrfToken })
    const deal = normalizeDeal(payload.deal)
    setServerSummary(null)
    setDeals((currentDeals) => [deal, ...currentDeals])
    return deal
  }

  const moveDeal = (id, targetStage) => {
    let previousDeals = []
    setDeals((currentDeals) => {
      previousDeals = currentDeals
      return currentDeals.map((deal) => (
        deal.id === id ? moveDealToStage(deal, targetStage) : deal
      ))
    })
    setServerSummary(null)
    setError(null)

    if (!canHydrate) return

    updateDealApi(id, updateDealPayload({ stage: targetStage }, 'manual'), { csrfToken })
      .then((payload) => {
        const persistedDeal = normalizeDeal(payload.deal)
        setDeals((currentDeals) => currentDeals.map((deal) => (
          deal.id === id ? persistedDeal : deal
        )))
      })
      .catch((updateError) => {
        setDeals(previousDeals)
        setError(updateError.message)
        toast.error(updateError.message)
      })
  }

  const updateDeal = (id, updates, options = {}) => {
    let previousDeals = []
    setDeals((currentDeals) => {
      previousDeals = currentDeals
      return currentDeals.map((deal) => (
        deal.id === id ? mergeDealUpdates(deal, updates) : deal
      ))
    })
    setServerSummary(null)
    setError(null)

    if (!canHydrate) return Promise.resolve(findDeal(id))

    return updateDealApi(id, updateDealPayload(updates, options.activitySource), { csrfToken })
      .then((payload) => {
        const persistedDeal = normalizeDeal(payload.deal)
        setDeals((currentDeals) => currentDeals.map((deal) => (
          deal.id === id ? persistedDeal : deal
        )))
        return persistedDeal
      })
      .catch((updateError) => {
        setDeals(previousDeals)
        setError(updateError.message)
        throw updateError
      })
  }

  const replaceDeal = (deal) => {
    const nextDeal = normalizeDeal(deal)
    setServerSummary(null)
    setDeals((currentDeals) => currentDeals.map((currentDeal) => (
      currentDeal.id === nextDeal.id ? nextDeal : currentDeal
    )))
  }

  const completePriorityAction = async (priorityId) => {
    setError(null)

    if (!canHydrate) return null

    const payload = await completeDealPriorityAction(priorityId, { csrfToken })
    await refreshDeals()
    return payload.priority
  }

  const dismissPriorityAction = async (priorityId) => {
    setError(null)

    if (!canHydrate) return null

    const payload = await dismissDealPriorityAction(priorityId, { csrfToken })
    await refreshDeals()
    return payload.priority
  }

  const snoozePriorityAction = async (priorityId, options = {}) => {
    setError(null)

    if (!canHydrate) return null

    const snoozeUntil = options.snoozeUntil ?? new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString()
    const payload = await snoozeDealPriorityAction(priorityId, { csrfToken, snoozeUntil })
    await refreshDeals()
    const persistedSnoozeUntil = payload.priority.snoozedUntil ?? snoozeUntil
    toast.success(`Priority snoozed for ${snoozeDurationLabel(persistedSnoozeUntil)}.`)
    return payload.priority
  }

  const deleteDeal = async (id, { confirmation } = {}) => {
    setError(null)

    if (canHydrate) {
      await deleteDealApi(id, { confirmation, csrfToken })
    }

    setServerSummary(null)
    setDeals((currentDeals) => currentDeals.filter((deal) => deal.id !== id))
    return null
  }

  const findDeal = (id) => deals.find((deal) => deal.id === id)

  return (
    <DealPipelineContext
      value={{
        deals,
        summary,
        isLoading: Boolean(shouldHydrate && isLoading),
        error: shouldHydrate ? error : null,
        addDeal,
        moveDeal,
        updateDeal,
        deleteDeal,
        replaceDeal,
        completePriorityAction,
        dismissPriorityAction,
        snoozePriorityAction,
        refreshDeals,
        findDeal,
      }}
    >
      {children}
    </DealPipelineContext>
  )
}
